import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import Header from "../components/Header";
import MapTracker from "../components/MapTracker";
import { CheckCircle2, Circle, Loader2, CreditCard, Home as HomeIcon, XCircle } from "lucide-react";

const NORRKOPING = { lat: 58.5942, lng: 16.1826 };

function statusStep(status) {
  const s = (status || "").toLowerCase();
  if (["cancelled", "canceled", "rejected", "declined"].includes(s)) return -1;
  if (["delivered", "completed", "done"].includes(s)) return 3;
  if (["picked_up", "picked", "on_the_way", "heading", "out_for_delivery", "delivering"].includes(s)) return 2;
  if (["accepted", "assigned"].includes(s)) return 1;
  return 0; // pending_approval, approved
}

function extractLatLng(obj) {
  if (!obj) return null;
  if (typeof obj.lat === "number" && typeof obj.lng === "number") return { lat: obj.lat, lng: obj.lng };
  if (typeof obj.latitude === "number" && typeof obj.longitude === "number") return { lat: obj.latitude, lng: obj.longitude };
  return null;
}

export default function TrackPage() {
  const { orderId } = useParams();
  const { t, guestId } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [payLoading, setPayLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const o = await api.getOrder(orderId);
      setOrder(o);
      return o;
    } catch { return null; }
  }, [orderId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  // Geocode dropoff once
  useEffect(() => {
    if (!order || dropoff) return;
    const dest = extractLatLng(order.destination);
    if (dest) { setDropoff(dest); return; }
    const addr = order.dropoff_address;
    if (!addr) { setDropoff(NORRKOPING); return; }
    (async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(addr + ", Sweden")}`, { headers: { "Accept-Language": "sv" } });
        const j = await r.json();
        if (j && j[0]) setDropoff({ lat: parseFloat(j[0].lat), lng: parseFloat(j[0].lon) });
        else setDropoff(NORRKOPING);
      } catch { setDropoff(NORRKOPING); }
    })();
  }, [order, dropoff]);

  // Stripe return
  useEffect(() => {
    if (params.get("payment") === "success") {
      toast.success(t.paid);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const step = statusStep(order?.status);
  const courier = order ? extractLatLng(order.courier_location || order.location) : null;
  const isCard = order?.payment?.method === "card";
  const paid = order?.payment?.status === "paid";

  const payNow = async () => {
    setPayLoading(true);
    try {
      const res = await api.checkout(orderId, { guest_id: guestId, origin: `${window.location.origin}/track/${orderId}` });
      if (res?.url) window.location.href = res.url;
      else toast.error("…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.payAfterAccept);
    } finally {
      setPayLoading(false);
    }
  };

  if (!order) {
    return (
      <div className="min-h-screen"><Header />
        <div className="grid place-items-center py-24 text-muted-foreground"><Loader2 className="h-8 w-8 animate-spin" /></div>
      </div>
    );
  }

  const cancelled = step === -1;

  return (
    <div className="min-h-screen pb-10">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-head text-2xl font-extrabold">{t.tracking}</h1>
            <div className="text-sm text-muted-foreground">{t.orderNr}: <span className="font-semibold text-foreground" data-testid="order-number">{order.order_number}</span></div>
          </div>
          <button onClick={() => navigate("/")} className="h-10 px-4 rounded-xl border border-border text-sm font-semibold flex items-center gap-1.5 hover:bg-accent">
            <HomeIcon className="h-4 w-4" /> {t.backHome}
          </button>
        </div>

        {/* Map */}
        <div className="rounded-2xl overflow-hidden border border-border h-64 sm:h-80 mb-5">
          <MapTracker dropoff={dropoff} courier={courier} />
        </div>

        {/* Status */}
        <section className="rounded-2xl border border-border bg-card p-5 mb-5" data-testid="order-status">
          <h2 className="font-head font-bold mb-4">{t.statusTitle}</h2>
          {cancelled ? (
            <div className="flex items-center gap-2 text-destructive font-semibold"><XCircle className="h-5 w-5" /> {t.cancelled}</div>
          ) : (
            <ol className="space-y-4">
              {t.steps.map((label, i) => {
                const done = i <= step;
                const current = i === step;
                return (
                  <li key={i} className="flex items-center gap-3">
                    {done ? <CheckCircle2 className={`h-6 w-6 ${current ? "text-primary" : "text-primary/70"}`} /> : <Circle className="h-6 w-6 text-muted-foreground/40" />}
                    <span className={`font-medium ${done ? "text-foreground" : "text-muted-foreground"} ${current ? "text-primary" : ""}`}>{label}</span>
                    {current && <Loader2 className="h-4 w-4 animate-spin text-primary ms-auto" />}
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        {/* Payment */}
        {isCard && !cancelled && (
          <section className="rounded-2xl border border-border bg-card p-5 mb-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-semibold"><CreditCard className="h-5 w-5 text-primary" /> {t.card}</div>
              {paid ? (
                <span className="px-3 py-1 rounded-full bg-primary/15 text-primary text-sm font-bold">{t.paid} ✓</span>
              ) : (
                <span className="px-3 py-1 rounded-full bg-amber-500/15 text-amber-600 text-sm font-semibold">{t.paymentPending}</span>
              )}
            </div>
            {!paid && (
              <>
                <button
                  data-testid="pay-card-button"
                  onClick={payNow}
                  disabled={payLoading}
                  className="mt-4 w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {payLoading ? <><Loader2 className="h-5 w-5 animate-spin" /> {t.openingPay}</> : <>{t.payWithCard} · {order.amount} {t.kr}</>}
                </button>
                {step < 1 && <p className="mt-2 text-xs text-muted-foreground text-center">{t.payAfterAccept}</p>}
              </>
            )}
          </section>
        )}

        {/* Items */}
        <section className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-head font-bold mb-3">{t.yourItems}</h2>
          <div className="space-y-2">
            {(order.cart_items || []).map((it, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium">{it.qty}× {it.name}</span>
                  {it.note && <span className="text-muted-foreground"> · {it.note}</span>}
                </div>
                <span className="text-muted-foreground">{it.price * it.qty} {t.kr}</span>
              </div>
            ))}
            <div className="flex justify-between font-bold pt-2 border-t border-border"><span>{t.total}</span><span>{order.amount} {t.kr}</span></div>
          </div>
        </section>
      </main>
    </div>
  );
}
